// /src/GuitarSimulator/GuitarCanvas.js

import { GuitarUtils } from './guitarUtils';
import { AudioUtils } from './audioAnalyzer';

export class GuitarCanvas {
  constructor(canvasRef, config) {
    this.canvasRef = canvasRef;
    this.config = {
      showNotes: true,
      selectedChord: null,
      pressedFret: null,
      pressedString: null,
      canvasTransform: { rotation: 180, flipX: false, flipY: false },
      maxFret: 21,
      isExternalInput: false,
      noteIntensities: {},
      chordDefinitions: {},
      alternativeChordPositions: {},
      useKoreanNotes: false,
      showFingerNumbers: false,
      chordPlayMode: 'primary',
      vibratingStrings: new Map(), // 진동 중인 현들과 시작 시간
      ...config
    };
  }

  updateConfig(newConfig) {
    this.config = { ...this.config, ...newConfig };
  }

  initCanvas() {
    const canvas = this.canvasRef.current;
    if (!canvas) return;

    canvas.width = canvas.offsetWidth * window.devicePixelRatio;
    canvas.height = canvas.offsetHeight * window.devicePixelRatio;
    const ctx = canvas.getContext('2d');
    ctx.scale(window.devicePixelRatio, window.devicePixelRatio);

    this.drawFretboard();
  }

  drawFretboard() {
    const canvas = this.canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const w = canvas.offsetWidth;
    const h = canvas.offsetHeight;

    // 좌표 변환 함수
    const transformPosition = (x, y) => {
      let newX = x;
      let newY = y;

      if (this.config.canvasTransform.flipX) {
        newX = w - x;
      }

      if (this.config.canvasTransform.flipY) {
        newY = h - y;
      }

      if (this.config.canvasTransform.rotation === 180) {
        newX = w - newX;
        newY = h - newY;
      }

      return { x: newX, y: newY };
    };

    // 캔버스 변환 적용
    ctx.save();
    ctx.translate(w/2, h/2);

    if (this.config.canvasTransform.rotation === 180) {
      ctx.rotate(Math.PI);
    }

    if (this.config.canvasTransform.flipX) {
      ctx.scale(-1, 1);
    }

    if (this.config.canvasTransform.flipY) {
      ctx.scale(1, -1);
    }

    ctx.translate(-w/2, -h/2);

    // 배경 그리기
    ctx.fillStyle = '#4b2c14';
    ctx.fillRect(0, 0, w, h);

    const x1 = GuitarUtils.getFretPosition(1, w, this.config.maxFret);
    ctx.fillStyle = '#1b110aff';
    ctx.fillRect(x1, 0, w-x1, h);

    // 프렛 선 그리기
    for (let i = 1; i <= this.config.maxFret + 1; i++) {
      const x = GuitarUtils.getFretPosition(i, w, this.config.maxFret);

      if (i === 1) {
        ctx.lineWidth = 12;
        ctx.strokeStyle = '#fcfcfcff';
      } else {
        ctx.strokeStyle = '#838282ff';
        ctx.lineWidth = 2;
      }
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, h);
      ctx.stroke();
    }

    // 현 그리기 (진동 효과 포함)
    const stringSpacing = h / 6;
    const now = Date.now();

    for (let i = 0; i < 6; i++) {
      let y = (i + 0.5) * stringSpacing;
      ctx.lineWidth = 1 + (5 - i) * 0.5;
      
      // 진동 중인 현인지 확인
      const vibrationData = this.config.vibratingStrings.get(i);
      let isVibrating = false;
      
      if (vibrationData) {
        const elapsed = now - vibrationData.startTime;
        if (elapsed < vibrationData.duration) {
          isVibrating = true;
          // 진동 효과 (사인파로 Y축 흔들림)
          const frequency = 25; // 진동 주파수 증가
          const amplitude = Math.max(0, 1.5 * (1 - elapsed / vibrationData.duration)); // 진폭 감소
          y += Math.sin((elapsed / 1000) * frequency * 2 * Math.PI) * amplitude;
          
          // 색상 페이드 아웃 효과
          const fadeRatio = Math.max(0, 1 - elapsed / vibrationData.duration);
          const goldIntensity = Math.max(0.3, fadeRatio); // 최소 30%까지만 페이드
          
          ctx.strokeStyle = `rgba(255, 215, 0, ${goldIntensity})`;
          ctx.shadowColor = `rgba(255, 215, 0, ${goldIntensity * 0.8})`;
          ctx.shadowBlur = 3 + (goldIntensity * 2);
        } else {
          // 진동 시간이 끝나면 제거
          this.config.vibratingStrings.delete(i);
        }
      } else {
        ctx.strokeStyle = '#e0e0e0';
        ctx.shadowBlur = 0;
      }
      
      // 색상 설정
      if (isVibrating) {
        // 진동 중인 현은 금색으로
        ctx.strokeStyle = '#FFD700';
        ctx.shadowColor = '#FFD700';
        ctx.shadowBlur = 5;
      } else {
        ctx.strokeStyle = '#e0e0e0';
        ctx.shadowBlur = 0;
      }
      
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(w, y);
      ctx.stroke();
      
      // 그림자 리셋
      ctx.shadowBlur = 0;
    }

    // 변환 상태 복원 (텍스트 그리기를 위해)
    ctx.restore();

    // 프렛 번호 표시
    this.drawFretNumbers(ctx, w, h, stringSpacing, transformPosition);

    // 음표 표시
    if (this.config.showNotes) {
      this.drawNotes(ctx, w, h, stringSpacing, transformPosition);
    }

    // 마커 점들 그리기
    this.drawMarkers(ctx, w, h);

    // 현재 누른 위치 표시
    this.drawPressedPosition(ctx, w, h, stringSpacing);
  }

  drawFretNumbers(ctx, w, h, stringSpacing, transformPosition) {
    ctx.font = `${Math.min(w, h) * 0.07}px Arial`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    const displayFrets_all = [3, 5, 7, 9, 12, 15, 17, 19];
    const displayFrets = displayFrets_all.filter(fret => fret <= this.config.maxFret);

    for (let fret of displayFrets) {
      const x = GuitarUtils.getFretPosition(fret, w, this.config.maxFret) - 
                (GuitarUtils.getFretPosition(fret, w, this.config.maxFret) - 
                 GuitarUtils.getFretPosition(fret + 1, w, this.config.maxFret)) / 2;
      const y = 3 * stringSpacing;

      const transformedPos = transformPosition(x, y);

      ctx.fillStyle = '#ffffff';
      ctx.fillText(fret.toString(), transformedPos.x, transformedPos.y);
    }
  }

  drawNotes(ctx, w, h, stringSpacing, transformPosition) {
    ctx.font = `${Math.min(w, h) * 0.06}px Arial`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    for (let stringIndex = 0; stringIndex < 6; stringIndex++) {
      for (let fret = 0; fret <= this.config.maxFret; fret++) {
        const note = GuitarUtils.calculateNote(stringIndex, fret);
        
        // 선택된 모드에 따라 표시할 코드 포지션 결정
        const isPrimaryChordNote = this.config.selectedChord && 
                                  this.config.chordPlayMode === 'primary' &&
                                  GuitarUtils.isChordNote(stringIndex, fret, this.config.selectedChord, this.config.chordDefinitions);

        // 대체 포지션도 같은 변환 로직 적용
        const chordDefStringNumber = 6 - stringIndex;
        const isAlternativeChordNote = this.config.selectedChord && 
                                      this.config.chordPlayMode === 'alternative' &&
                                      this.config.alternativeChordPositions[this.config.selectedChord] &&
                                      this.config.alternativeChordPositions[this.config.selectedChord].some(pos =>
                                        pos.string === chordDefStringNumber && pos.fret === fret);

        const isChordNote = isPrimaryChordNote || isAlternativeChordNote;

        // 외부 입력이 있고 해당 음에 강도가 있으면 샤프 음도 표시
        const hasExternalIntensity = this.config.isExternalInput && 
                                    this.config.noteIntensities[note] && 
                                    this.config.noteIntensities[note] > 0.15;

        // 표시 조건: 자연음 OR 코드 구성음 OR 외부 입력으로 감지된 음
        if (!GuitarUtils.isNaturalNote(note) && !isChordNote && !hasExternalIntensity) {
          continue;
        }
        
        // 표시할 텍스트 결정
        let displayText = '';
        
        if (this.config.showFingerNumbers && this.config.selectedChord) {
          // 손가락 번호 모드이고 코드가 선택된 경우
          const chordPositions = this.config.chordDefinitions[this.config.selectedChord] || [];
          const alternativePositions = this.config.alternativeChordPositions[this.config.selectedChord] || [];
          
          // guitarUtils 인덱스를 chordDefinitions 인덱스로 변환
          const chordDefStringNumber = 6 - stringIndex;
          
          // 먼저 기본 포지션 확인
          let chordPosition = chordPositions.find(pos => pos.string === chordDefStringNumber && pos.fret === fret);
          
          // 기본 포지션이 없으면 대체 포지션 확인
          if (!chordPosition) {
            chordPosition = alternativePositions.find(pos => pos.string === chordDefStringNumber && pos.fret === fret);
          }
          
          if (chordPosition && chordPosition.finger !== undefined) {
            displayText = chordPosition.finger === 0 ? 'O' : chordPosition.finger.toString();
          }
        } else {
          // 일반 음계 표시 모드
          if (GuitarUtils.isNaturalNote(note)) {
            displayText = GuitarUtils.getDisplayNote(note, this.config.useKoreanNotes);
          } else if (isChordNote || hasExternalIntensity) {
            displayText = GuitarUtils.getDisplayNote(note, this.config.useKoreanNotes);
          }
        }

        const x = GuitarUtils.getFretPosition(fret, w, this.config.maxFret) - 
                  (GuitarUtils.getFretPosition(fret, w, this.config.maxFret) - 
                   GuitarUtils.getFretPosition(fret + 1, w, this.config.maxFret)) / 2;
        const y = (stringIndex + 0.5) * stringSpacing;

        const transformedPos = transformPosition(x, y);

        let bgColor = 'rgba(122, 114, 114, 0.8)';
        let glowEffect = 'none';
        
        if (this.config.isExternalInput && this.config.noteIntensities[note]) {
          const intensity = this.config.noteIntensities[note];
          // 임계값을 0.15로 상향 조정하여 작은 소리 필터링
          if (intensity > 0.15) { 
            bgColor = AudioUtils.intensityToBrightness(intensity, '#7a7272');
            glowEffect = AudioUtils.getGlowEffect(intensity);
          }
        } else {
            if (isPrimaryChordNote) {
                // 기본 포지션은 초록색
                bgColor = 'rgba(76, 175, 80, 0.9)';
                glowEffect = '0 0 8px 2px rgba(76, 175, 80, 0.6)';
            } else if (isAlternativeChordNote) {
                // 대체 포지션은 분홍색
                bgColor = 'rgba(233, 30, 99, 0.9)';
                glowEffect = '0 0 8px 2px rgba(233, 30, 99, 0.6)';
            } else if (displayText === 'C' || displayText === '도') {
                bgColor = 'rgba(22, 22, 21, 0.8)';
            }
        }

        // 배경 원 그리기 (자연음은 항상 표시)
        ctx.save();
        
        // 글로우 효과 적용
        if (glowEffect !== 'none') {
          ctx.shadowColor = 'rgba(255, 255, 255, 0.8)';
          ctx.shadowBlur = 10;
        }
        
        ctx.fillStyle = bgColor;
        ctx.beginPath();
        ctx.arc(transformedPos.x, transformedPos.y, Math.min(w, h) * 0.05, 0, Math.PI * 2);
        ctx.fill();
        
        ctx.restore();

        // 강도가 높은 경우 추가 시각 효과
        if (this.config.isExternalInput && this.config.noteIntensities[note] > 0.7) {
          const intensity = this.config.noteIntensities[note];
          const pulseSize = Math.min(w, h) * 0.02 * (intensity - 0.7) / 0.3;
          
          ctx.save();
          ctx.globalAlpha = 0.3 + (intensity - 0.7) * 0.5;
          ctx.strokeStyle = bgColor;
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.arc(transformedPos.x, transformedPos.y, Math.min(w, h) * 0.05 + pulseSize, 0, Math.PI * 2);
          ctx.stroke();
          ctx.restore();
        }

        if (isPrimaryChordNote) {
          ctx.strokeStyle = 'rgba(76, 175, 80, 1)';
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.arc(transformedPos.x, transformedPos.y, Math.min(w, h) * 0.04, 0, Math.PI * 2);
          ctx.stroke();
        } else if (isAlternativeChordNote) {
          ctx.strokeStyle = 'rgba(233, 30, 99, 1)';
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.arc(transformedPos.x, transformedPos.y, Math.min(w, h) * 0.04, 0, Math.PI * 2);
          ctx.stroke();
        }

        // 텍스트 색상 설정
        let textColor = '#000000'; // 기본 검은색
        let strokeColor = '#ffffff';
        
        // 코드가 선택되고 현재 음표가 코드에 포함된 경우
        if (this.config.selectedChord && 
            GuitarUtils.isChordNote(stringIndex, fret, this.config.selectedChord, this.config.chordDefinitions)) {
          textColor = '#ffffff'; // 코드 선택 시 흰색
          strokeColor = '#000000';
        }
        // 외부 입력이 없고 C음/도인 경우
        else if (!this.config.isExternalInput && (displayText === 'C' || displayText === '도')) {
          textColor = '#ffffff'; // C음은 흰색
          strokeColor = '#000000';
        }
        // 외부 입력으로 강조된 경우
        else if (this.config.isExternalInput && this.config.noteIntensities[note] > 0.5) {
          textColor = '#ffffff'; // 강조 시 흰색
          strokeColor = '#000000';
        }
        
        ctx.fillStyle = textColor;
        
        // 강도가 높은 경우 텍스트도 강조
        if (this.config.isExternalInput && this.config.noteIntensities[note] > 0.5) {
          ctx.font = `bold ${Math.min(w, h) * 0.06}px Arial`;
          ctx.strokeStyle = strokeColor;
          ctx.lineWidth = 1;
          ctx.strokeText(displayText, transformedPos.x, transformedPos.y);
        }
        
        ctx.fillText(displayText, transformedPos.x, transformedPos.y);
      }
    }
  }

  drawMarkers(ctx, w, h) {
    // 다시 변환 적용 (마커와 눌린 위치 표시를 위해)
    ctx.save();
    ctx.translate(w/2, h/2);

    if (this.config.canvasTransform.rotation === 180) {
      ctx.rotate(Math.PI);
    }

    if (this.config.canvasTransform.flipX) {
      ctx.scale(-1, 1);
    }

    if (this.config.canvasTransform.flipY) {
      ctx.scale(1, -1);
    }

    ctx.translate(-w/2, -h/2);

    // 기존 마커 점들
    ctx.fillStyle = 'rgba(255, 215, 0, 0.6)';
    const markerFrets_all = [3, 5, 7, 9, 15, 17, 19, 21];
    const markerFrets = markerFrets_all.filter(fret => fret <= this.config.maxFret);

    const radius = Math.min(w, h) * 0.012;

    markerFrets.forEach(fret => {
      const x = GuitarUtils.getFretPosition(fret, w, this.config.maxFret) - 
                (GuitarUtils.getFretPosition(fret, w, this.config.maxFret) - 
                 GuitarUtils.getFretPosition(fret + 1, w, this.config.maxFret)) / 2;
      const y = h / 2;
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.fill();
    });

    // 12프렛 더블 마커
    if (this.config.maxFret >= 12) {
      const x12 = GuitarUtils.getFretPosition(12, w, this.config.maxFret) - 
                  (GuitarUtils.getFretPosition(12, w, this.config.maxFret) - 
                   GuitarUtils.getFretPosition(13, w, this.config.maxFret)) / 2;
      ctx.beginPath();
      ctx.arc(x12, h / 3, radius, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(x12, (h * 2) / 3, radius, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
  }

  drawPressedPosition(ctx, w, h, stringSpacing) {
    // 현재 눌린 위치 표시
    if (this.config.pressedFret !== null && this.config.pressedString !== null) {
      ctx.save();
      ctx.translate(w/2, h/2);

      if (this.config.canvasTransform.rotation === 180) {
        ctx.rotate(Math.PI);
      }

      if (this.config.canvasTransform.flipX) {
        ctx.scale(-1, 1);
      }

      if (this.config.canvasTransform.flipY) {
        ctx.scale(1, -1);
      }

      ctx.translate(-w/2, -h/2);

      const fretX = GuitarUtils.getFretPosition(this.config.pressedFret, w, this.config.maxFret) - 
                    (GuitarUtils.getFretPosition(this.config.pressedFret, w, this.config.maxFret) - 
                     GuitarUtils.getFretPosition(this.config.pressedFret + 1, w, this.config.maxFret)) / 2;
      const stringY = (this.config.pressedString + 0.5) * stringSpacing;

      ctx.fillStyle = 'rgba(255, 0, 0, 0.8)';
      ctx.beginPath();
      ctx.arc(fretX, stringY, Math.min(w, h) * 0.025, 0, Math.PI * 2);
      ctx.fill();

      ctx.strokeStyle = 'rgba(255, 0, 0, 1)';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(fretX, stringY, Math.min(w, h) * 0.03, 0, Math.PI * 2);
      ctx.stroke();

      ctx.restore();
    }
  }

  // 현 진동 시작
  startStringVibration(stringIndex, duration = 100) { // 2000 → 1200으로 단축
    this.config.vibratingStrings.set(stringIndex, {
      startTime: Date.now(),
      duration: duration
    });
  }

  // 모든 진동 중지
  stopAllVibrations() {
    this.config.vibratingStrings.clear();
  }

  // 특정 현 진동 중지
  stopStringVibration(stringIndex) {
    this.config.vibratingStrings.delete(stringIndex);
  }
}